import { datenblattFromSchema } from '../forms/datenblattFromSchema'

const fallbackUeStepColumns = [
  'Vertrieb2PE',
  'PE2Vertrieb',
  'Vertrieb2ENG',
  'ENG2Vertrieb',
  'CompletionOfPlanning',
  'Vertrieb2Projektleitung',
  'Projektleitung2SuW',
  'SuW2Running',
  'preliminaryHandover_Projektleitung2SuW',
  'finalHandover_Projektleitung2SuW',
] as const

const hiddenUeStepColumns = new Set(['CompletionOfPlanning2Vertrieb'])

const systemFields = new Set(['processType', 'processUebergabeStep'])

type RequiredFieldValue = string | null | undefined
type RequiredFieldsData = Record<string, Record<string, RequiredFieldValue> | undefined>

function getStepColumns(data: RequiredFieldsData, stepOrder: readonly string[] = fallbackUeStepColumns) {
  const responseSteps = Object.keys(data).filter((step) =>
    !hiddenUeStepColumns.has(step) && data[step] && typeof data[step] === 'object'
  )
  if (responseSteps.length === 0) {
    return [...stepOrder]
  }

  const orderedKnownSteps = stepOrder.filter((step) => responseSteps.includes(step))
  const remainingSteps = responseSteps.filter((step) => !orderedKnownSteps.includes(step))
  return [...orderedKnownSteps, ...remainingSteps]
}

function escapeXml(value: unknown) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
}

function columnName(index: number) {
  let name = ''
  let value = index

  while (value > 0) {
    const remainder = (value - 1) % 26
    name = String.fromCharCode(65 + remainder) + name
    value = Math.floor((value - 1) / 26)
  }

  return name
}

function cellXml(rowIndex: number, columnIndex: number, value: unknown, style = 0) {
  const ref = `${columnName(columnIndex)}${rowIndex}`
  const styleAttr = style ? ` s="${style}"` : ''
  return `<c r="${ref}" t="inlineStr"${styleAttr}><is><t>${escapeXml(value)}</t></is></c>`
}

function rowXml(rowIndex: number, values: unknown[], style = 0) {
  return `<row r="${rowIndex}">${values.map((value, idx) => cellXml(rowIndex, idx + 1, value, style)).join('')}</row>`
}

function buildRows(
  data: RequiredFieldsData,
  labelForField: (fieldName: string) => string,
  labelForStep: (stepName: string) => string,
  labelForValue: (value: RequiredFieldValue) => string,
  stepOrder?: readonly string[]
) {
  const rows: Array<{ style: number; values: string[] }> = []
  const stepColumns = getStepColumns(data, stepOrder)
  const stepLabels = stepColumns.map(labelForStep)
  const groupEntries = Object.entries(datenblattFromSchema)
  const groupedFields = new Set(groupEntries.flatMap(([, group]) => group.fields))

  for (const [groupName, group] of groupEntries) {
    rows.push({ style: 1, values: [groupName, ...stepLabels] })

    for (const field of group.fields) {
      rows.push({
        style: 0,
        values: [labelForField(field), ...stepColumns.map((step) => labelForValue(data[step]?.[field]))],
      })
    }

    rows.push({ style: 0, values: [] })
  }

  const ungroupedFields = new Set<string>()
  for (const step of stepColumns) {
    for (const field of Object.keys(data[step] || {})) {
      if (!systemFields.has(field) && !groupedFields.has(field)) {
        ungroupedFields.add(field)
      }
    }
  }

  if (ungroupedFields.size > 0) {
    rows.push({ style: 1, values: ['Nicht im UI gruppiert', ...stepLabels] })

    for (const field of Array.from(ungroupedFields).sort()) {
      rows.push({
        style: 0,
        values: [labelForField(field), ...stepColumns.map((step) => labelForValue(data[step]?.[field]))],
      })
    }
  }

  return { rows, columnCount: stepColumns.length + 1 }
}

function buildWorksheetXml(rows: Array<{ style: number; values: string[] }>, columnCount: number) {
  const sheetRows = rows
    .map((row, idx) => row.values.length === 0 ? `<row r="${idx + 1}"/>` : rowXml(idx + 1, row.values, row.style))
    .join('')
  const lastColumn = columnName(columnCount)

  return [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">',
    '<sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>',
    '<cols>',
    '<col min="1" max="1" width="42" customWidth="1"/>',
    `<col min="2" max="${columnCount}" width="24" customWidth="1"/>`,
    '</cols>',
    `<sheetData>${sheetRows}</sheetData>`,
    `<autoFilter ref="A1:${lastColumn}1"/>`,
    '</worksheet>',
  ].join('')
}

function buildWorkbookXml(sheetName: string) {
  return [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">',
    `<sheets><sheet name="${escapeXml(sheetName)}" sheetId="1" r:id="rId1"/></sheets>`,
    '</workbook>',
  ].join('')
}

function buildWorkbookRelsXml() {
  return [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">',
    '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>',
    '<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>',
    '</Relationships>',
  ].join('')
}

function buildRootRelsXml() {
  return [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">',
    '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>',
    '</Relationships>',
  ].join('')
}

function buildContentTypesXml() {
  return [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">',
    '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>',
    '<Default Extension="xml" ContentType="application/xml"/>',
    '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>',
    '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>',
    '<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>',
    '</Types>',
  ].join('')
}

function buildStylesXml() {
  return [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">',
    '<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/><color rgb="FFFFFFFF"/></font></fonts>',
    '<fills count="3"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill><fill><patternFill patternType="solid"><fgColor rgb="FF303030"/><bgColor indexed="64"/></patternFill></fill></fills>',
    '<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>',
    '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>',
    '<cellXfs count="2"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/><xf numFmtId="0" fontId="1" fillId="2" borderId="0" xfId="0" applyFont="1" applyFill="1"/></cellXfs>',
    '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>',
    '<dxfs count="0"/>',
    '<tableStyles count="0" defaultTableStyle="TableStyleMedium9" defaultPivotStyle="PivotStyleLight16"/>',
    '</styleSheet>',
  ].join('')
}

function crc32(buffer: Uint8Array) {
  let crc = 0xffffffff

  for (const byte of buffer) {
    crc ^= byte
    for (let idx = 0; idx < 8; idx += 1) {
      crc = (crc >>> 1) ^ (0xedb88320 & -(crc & 1))
    }
  }

  return (crc ^ 0xffffffff) >>> 0
}

function dosDateTime(date: Date) {
  return {
    dosTime: (date.getHours() << 11) | (date.getMinutes() << 5) | Math.floor(date.getSeconds() / 2),
    dosDate: ((date.getFullYear() - 1980) << 9) | ((date.getMonth() + 1) << 5) | date.getDate(),
  }
}

function writeUInt16LE(buffer: Uint8Array, offset: number, value: number) {
  buffer[offset] = value & 0xff
  buffer[offset + 1] = (value >>> 8) & 0xff
}

function writeUInt32LE(buffer: Uint8Array, offset: number, value: number) {
  buffer[offset] = value & 0xff
  buffer[offset + 1] = (value >>> 8) & 0xff
  buffer[offset + 2] = (value >>> 16) & 0xff
  buffer[offset + 3] = (value >>> 24) & 0xff
}

function concatUint8Arrays(parts: Uint8Array[]) {
  const totalLength = parts.reduce((sum, part) => sum + part.length, 0)
  const result = new Uint8Array(totalLength)
  let offset = 0

  for (const part of parts) {
    result.set(part, offset)
    offset += part.length
  }

  return result
}

function createZip(files: Array<{ name: string; content: string }>) {
  const encoder = new TextEncoder()
  const localParts: Uint8Array[] = []
  const centralParts: Uint8Array[] = []
  const now = dosDateTime(new Date())
  let offset = 0

  for (const file of files) {
    const nameBuffer = encoder.encode(file.name)
    const contentBuffer = encoder.encode(file.content)
    const crc = crc32(contentBuffer)

    const localHeader = new Uint8Array(30)
    writeUInt32LE(localHeader, 0, 0x04034b50)
    writeUInt16LE(localHeader, 4, 20)
    writeUInt16LE(localHeader, 6, 0x0800)
    writeUInt16LE(localHeader, 8, 0)
    writeUInt16LE(localHeader, 10, now.dosTime)
    writeUInt16LE(localHeader, 12, now.dosDate)
    writeUInt32LE(localHeader, 14, crc)
    writeUInt32LE(localHeader, 18, contentBuffer.length)
    writeUInt32LE(localHeader, 22, contentBuffer.length)
    writeUInt16LE(localHeader, 26, nameBuffer.length)
    writeUInt16LE(localHeader, 28, 0)

    localParts.push(localHeader, nameBuffer, contentBuffer)

    const centralHeader = new Uint8Array(46)
    writeUInt32LE(centralHeader, 0, 0x02014b50)
    writeUInt16LE(centralHeader, 4, 20)
    writeUInt16LE(centralHeader, 6, 20)
    writeUInt16LE(centralHeader, 8, 0x0800)
    writeUInt16LE(centralHeader, 10, 0)
    writeUInt16LE(centralHeader, 12, now.dosTime)
    writeUInt16LE(centralHeader, 14, now.dosDate)
    writeUInt32LE(centralHeader, 16, crc)
    writeUInt32LE(centralHeader, 20, contentBuffer.length)
    writeUInt32LE(centralHeader, 24, contentBuffer.length)
    writeUInt16LE(centralHeader, 28, nameBuffer.length)
    writeUInt16LE(centralHeader, 30, 0)
    writeUInt16LE(centralHeader, 32, 0)
    writeUInt16LE(centralHeader, 34, 0)
    writeUInt16LE(centralHeader, 36, 0)
    writeUInt32LE(centralHeader, 38, 0)
    writeUInt32LE(centralHeader, 42, offset)

    centralParts.push(centralHeader, nameBuffer)
    offset += localHeader.length + nameBuffer.length + contentBuffer.length
  }

  const centralDirectory = concatUint8Arrays(centralParts)
  const endRecord = new Uint8Array(22)
  writeUInt32LE(endRecord, 0, 0x06054b50)
  writeUInt16LE(endRecord, 4, 0)
  writeUInt16LE(endRecord, 6, 0)
  writeUInt16LE(endRecord, 8, files.length)
  writeUInt16LE(endRecord, 10, files.length)
  writeUInt32LE(endRecord, 12, centralDirectory.length)
  writeUInt32LE(endRecord, 16, offset)
  writeUInt16LE(endRecord, 20, 0)

  return concatUint8Arrays([...localParts, centralDirectory, endRecord])
}

export function buildRequiredFieldsExcelBlob(
  data: RequiredFieldsData,
  sheetName: string,
  labelForField: (fieldName: string) => string,
  labelForStep: (stepName: string) => string = (stepName) => stepName,
  labelForValue: (value: RequiredFieldValue) => string = (value) => String(value ?? ''),
  stepOrder?: readonly string[]
) {
  const { rows, columnCount } = buildRows(data, labelForField, labelForStep, labelForValue, stepOrder)
  const files = [
    { name: '[Content_Types].xml', content: buildContentTypesXml() },
    { name: '_rels/.rels', content: buildRootRelsXml() },
    { name: 'xl/workbook.xml', content: buildWorkbookXml(sheetName) },
    { name: 'xl/_rels/workbook.xml.rels', content: buildWorkbookRelsXml() },
    { name: 'xl/styles.xml', content: buildStylesXml() },
    { name: 'xl/worksheets/sheet1.xml', content: buildWorksheetXml(rows, columnCount) },
  ]

  return new Blob([createZip(files)], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}
